import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";

interface LinePart {
  text: string;
  accent?: boolean;
}

// Each SlideLine is an array of parts rendered inline on the same line
type SlideLine = LinePart[];

interface Slide {
  tag: string;
  lines: SlideLine[];
  subtitle: string;
}

function useSlides(): Slide[] {
  const { t } = useTranslation();

  return [
    {
      tag: t("heroTag"),
      lines: [
        [{ text: t("heroTitle1") }],
        [{ text: t("heroTitle2") }],
        [{ text: t("heroTitle3"), accent: true }],
        [{ text: t("heroTitle4") }],
      ],
      subtitle: t("heroSubtitle"),
    },
    {
      tag: t("aboutHeroTag"),
      lines: [
        [{ text: t("aboutHeroTitle1") + " " }, { text: "Business", accent: true }],
        [{ text: "in East Africa", accent: true }],
      ],
      subtitle: t("aboutHeroDesc"),
    },
    {
      tag: t("servicesHeroTag"),
      lines: [
        [{ text: t("servicesHeroTitle1") }],
        [{ text: t("servicesHeroTitle2"), accent: true }],
      ],
      subtitle: t("servicesHeroDesc"),
    },
    {
      tag: t("bizHeroTag"),
      lines: [
        [{ text: t("bizHeroTitle1") }],
        [{ text: t("bizHeroTitle2"), accent: true }],
      ],
      subtitle: t("bizHeroDesc"),
    },
    {
      tag: t("eventHeroTag"),
      lines: [
        [{ text: t("eventHeroTitle1") }],
        [{ text: t("eventHeroTitle2"), accent: true }],
      ],
      subtitle: t("eventHeroDesc"),
    },
    {
      tag: t("tenderHeroTag"),
      lines: [
        [{ text: t("tenderHeroTitle1") }],
        [{ text: t("tenderHeroTitle2"), accent: true }],
      ],
      subtitle: t("tenderHeroDesc"),
    },
    {
      tag: t("contactHeroTag"),
      lines: [
        [{ text: t("contactHeroTitle1") }],
        [{ text: t("contactHeroTitle2"), accent: true }],
      ],
      subtitle: t("contactHeroDesc"),
    },
  ];
}

const SLIDE_DURATION = 30000;
const EXIT_DURATION = 450;

export default function HeroCarousel() {
  const slides = useSlides();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [phase, setPhase] = useState<"in" | "out">("in");
  const [displayIndex, setDisplayIndex] = useState(0);

  const advance = useCallback(() => {
    setPhase("out");
    setTimeout(() => {
      setCurrentIndex((prev) => {
        const next = (prev + 1) % slides.length;
        setDisplayIndex(next);
        return next;
      });
      setPhase("in");
    }, EXIT_DURATION);
  }, [slides.length]);

  useEffect(() => {
    const timer = setInterval(advance, SLIDE_DURATION);
    return () => clearInterval(timer);
  }, [advance]);

  const slide = slides[displayIndex];

  return (
    <div className="space-y-5">
      {/* Tag pill */}
      <div
        key={`tag-${currentIndex}`}
        className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#00C2C2]/30 bg-[#00C2C2]/10 ${phase === "in" ? "hero-text-in" : "hero-text-out"
          }`}
      >
        <span className="w-2 h-2 rounded-full bg-[#00C2C2] animate-pulse" />
        <span className="text-sm text-[#00C2C2] font-medium tracking-wide">
          {slide.tag}
        </span>
      </div>

      {/* Headline lines */}
      <div key={`headline-${currentIndex}`}>
        <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[1.05] tracking-tight">
          {slide.lines.map((line, i) => (
            <span
              key={i}
              className={`block ${phase === "in" ? "hero-text-in" : "hero-text-out"}`}
              style={{
                animationDelay: phase === "in" ? `${i * 60}ms` : `${i * 30}ms`,
                ...(phase === "in" ? { opacity: 0 } : {}),
              }}
            >
              {line.map((part, j) => (
                <span
                  key={j}
                  className={part.accent ? "text-[#00C2C2]" : "text-white"}
                >
                  {part.text}
                </span>
              ))}
            </span>
          ))}
        </h1>
      </div>

      {/* Subtitle */}
      <p
        key={`sub-${currentIndex}`}
        className={`text-lg text-white/60 leading-relaxed max-w-lg ${phase === "in" ? "hero-sub-in" : "hero-sub-out"
          }`}
      >
        {slide.subtitle}
      </p>

      {/* Dot indicators */}
      <div className="flex gap-1.5 pt-1">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => {
              if (i === currentIndex) return;
              setPhase("out");
              setTimeout(() => {
                setCurrentIndex(i);
                setDisplayIndex(i);
                setPhase("in");
              }, EXIT_DURATION);
            }}
            className={`cursor-pointer h-1 rounded-full transition-all duration-500 ${i === displayIndex
                ? "bg-[#00C2C2] w-6"
                : "bg-white/25 w-1.5 hover:bg-white/40"
              }`}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}